/**
 * Connect's own customer database, as staff read and decide it
 * (`/crm/connect-customers`, `docs/37_CENTRAL_CUSTOMER_DATABASE.md`).
 *
 * A customer here is Connect's: one person, with every number, email and
 * address any client's records taught Connect, and a link to each client's
 * contact of them. The clients' contacts themselves are never changed from
 * here. Every decision is checked on the server — the permission, the
 * organization-wide scope it needs, the state of the case.
 */

import { api, type Tone } from '@connect/web-shared';

export interface ClientRef {
  public_id: string;
  name: string;
  code: string;
}

export interface Identifier {
  public_id: string;
  kind: 'mobile' | 'email';
  value: string;
  status: 'observed' | 'verified';
  verified_at: string | null;
  verified_via: string | null;
  sources: number;
  held_since: string;
  first_seen_at: string;
  last_seen_at: string;
  carried_by: { client: ClientRef; role: 'primary' | 'secondary'; active: boolean }[];
}

export interface Address {
  public_id: string;
  line: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  sources: number;
  first_seen_at: string;
  last_seen_at: string;
}

export interface Link {
  public_id: string;
  status: 'confirmed' | 'provisional' | 'ended';
  basis: 'mobile' | 'email' | 'secondary' | 'new' | 'staff';
  locked: boolean;
  observed_name: string | null;
  linked_at: string;
  ended_at: string | null;
  client: ClientRef;
  contact: { public_id: string; name: string; mobile: string | null; email: string | null; deleted: boolean } | null;
}

export interface CustomerRef {
  public_id: string;
  name: string | null;
  status: string;
}

export type Confidence = 'none' | 'low' | 'medium' | 'high' | 'staff';

export interface CustomerRow {
  public_id: string;
  name: string | null;
  name_confidence: Confidence;
  status: 'active' | 'merged';
  mobiles: string[];
  emails: string[];
  verified: boolean;
  link_count: number;
  client_count: number;
  open_reviews: number;
  first_seen_at: string;
  last_seen_at: string;
}

export interface CustomerDetail {
  public_id: string;
  name: string | null;
  name_confidence: Confidence;
  name_locked: boolean;
  status: 'active' | 'merged';
  merged_into: CustomerRef | null;
  merged_from: CustomerRef[];
  link_count: number;
  client_count: number;
  open_reviews: number;
  first_seen_at: string;
  last_seen_at: string;
  identifiers: Identifier[];
  addresses: Address[];
  links: Link[];
}

export interface Party {
  public_id: string;
  name: string | null;
  name_confidence: Confidence;
  status: string;
  identifiers: Identifier[];
  links: Link[];
}

export interface Review {
  public_id: string;
  kind: 'same_person' | 'name_conflict';
  status: string;
  evidence: Record<string, unknown>;
  note: string | null;
  created_at: string;
  resolved_at: string | null;
  customer: Party;
  other: Party | null;
  link: Link | null;
}

export interface IdentityEvent {
  kind: string;
  created_at: string;
  customer: CustomerRef;
  other: CustomerRef | null;
  actor: string | null;
  detail: Record<string, unknown>;
}

export interface IdentityStatus {
  enabled: boolean;
  queued: number;
  failing: number;
  customers: number;
  merged: number;
  identifiers: number;
  verified: number;
  links: number;
  open_reviews: number;
  last_verified_at: string | null;
}

export type Decision = 'merge' | 'keep_apart' | 'same_person' | 'different_person';

export const BASE = '/crm/connect-customers';

export const identityKeys = {
  all: ['connect-customers'] as const,
  register: ['connect-customers', 'register'] as const,
  status: ['connect-customers', 'status'] as const,
  reviews: ['connect-customers', 'reviews'] as const,
  events: ['connect-customers', 'events'] as const,
  customer: (id: string) => ['connect-customers', 'customer', id] as const,
  history: (id: string) => ['connect-customers', 'customer', id, 'history'] as const,
};

export const identityApi = {
  status: () => api.get<IdentityStatus>(`${BASE}/status`),
  customer: (id: string) => api.get<CustomerDetail>(`${BASE}/${id}`),
  reviews: (params: Record<string, string | number | undefined>) => api.page<Review>(`${BASE}/reviews`, { params }),
  resolve: (id: string, body: { decision: Decision; move_identifier?: boolean; note?: string }) =>
    api.post<{ decision: Decision; customer: string }>(`${BASE}/reviews/${id}/resolve`, body),
  setName: (id: string, name: string | null) => api.put<CustomerDetail>(`${BASE}/${id}/name`, { name }),
  merge: (id: string, other: string, note?: string) => api.post<CustomerDetail>(`${BASE}/${id}/merge`, { other, note }),
  unmerge: (id: string) => api.post<CustomerDetail>(`${BASE}/${id}/unmerge`, {}),
  split: (id: string, links: string[], note?: string) => api.post<CustomerDetail>(`${BASE}/${id}/split`, { links, note }),
  erase: (id: string) => api.delete<{ identifiers: string[]; links: number }>(`${BASE}/${id}`),
  rebuild: () => api.post<{ queued: number }>(`${BASE}/rebuild`, {}),
};

/** How sure Connect is of the name, in words staff read at a glance. */
export const CONFIDENCE: Record<Confidence, { label: string; tone: Tone }> = {
  high: { label: 'Sure', tone: 'success' },
  medium: { label: 'Likely', tone: 'accent' },
  low: { label: 'Unsure', tone: 'warning' },
  none: { label: 'No name', tone: 'neutral' },
  staff: { label: 'Set by staff', tone: 'info' },
};

export const LINK_STATUS: Record<Link['status'], { label: string; tone: Tone }> = {
  confirmed: { label: 'Linked', tone: 'success' },
  provisional: { label: 'In doubt', tone: 'warning' },
  ended: { label: 'Deleted by client', tone: 'neutral' },
};

export const BASIS: Record<Link['basis'], string> = {
  mobile: 'Same mobile',
  email: 'Same email',
  secondary: 'Another number of theirs',
  new: 'First record of them',
  staff: 'Placed by staff',
};

/** A stored E.164 Indian number as people read it: `+91 98480 22331`. */
export function showMobile(value: string): string {
  const match = /^\+91(\d{5})(\d{5})$/.exec(value);
  return match ? `+91 ${match[1]} ${match[2]}` : value;
}

export function showIdentifier(identifier: Pick<Identifier, 'kind' | 'value'>): string {
  return identifier.kind === 'mobile' ? showMobile(identifier.value) : identifier.value;
}
